import React, { Suspense, useMemo, useState } from 'react';
import { Plus, ExternalLink, ChevronRight } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Loading, toast } from '@cotera/client/app/components';
import { ModalPageView } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { emitToolCredentialUpdated } from '@cotera/client/app/etc/emit-tool-credential-updated';
import {
  ProviderGroup,
  AvailableTool,
} from '@cotera/client/app/hooks/use-available-tools';
import { ToggleSelect } from '@cotera/client/app/components/forms/toggle/toggle';
import {
  CredentialForm,
  ToolInfoCard,
  type AuthStrategyInfo,
} from '@cotera/client/app/components/tools';
import { isCredentialStrategyOauthConnect } from '@cotera/api';
import { Accordion } from '../ui/accordion';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible';
import { cn } from '../../lib/utils';
import { Chip } from '@cotera/client/app/components/ui';
import { Assert } from '@cotera/utilities';
import { providerReadiness, toolMatchesFilter } from './catalog-model';
import { PROVIDER_ICONS } from './constants';
import {
  isLegacyStrategy,
  getProviderDocsUrl,
  getAuthStrategyLabel,
  filterStrategies,
} from './utils';
import { useToolCredentialInfo, useProviderAccounts } from './hooks';
import { ConnectedStatusCard } from './credential-connection-cards';
import { ProviderAccountCards } from './provider-account-cards';
import { CredentialStrategySection } from './credential-strategy-section';
import { CollapsibleSearch } from './ui';
import { CUSTOM_CREDENTIAL_STRATEGY_VIEWS } from './credential-strategy-views';
import { SlackCocoInstalledBadge } from './slack-coco-connection-status';
import {
  ToolConnectionStatusBadge,
  ToolConnectionStatusCard,
} from './tool-connection-status';
import { usesCocoSlackInstall } from './slack-provider';

const SLACK_OAUTH_STRATEGY = 'slack_oauth';
const SLACK_AUTH_STRATEGY = 'slack_auth';
const SLACK_SEARCH_COCO_INSTALL_STRATEGY = 'slack_search_coco_install';

/** Preferred order for Slack provider auth toggles (lower = first). */
const SLACK_AUTH_STRATEGY_ORDER: readonly string[] = [
  SLACK_AUTH_STRATEGY,
  SLACK_OAUTH_STRATEGY,
  'user_provided',
  'api_key',
  'apikey',
];

const sortSlackAuthStrategies = <T extends { name: string }>(
  strategies: T[]
): T[] => {
  return [...strategies].sort((a, b) => {
    const aIndex = SLACK_AUTH_STRATEGY_ORDER.indexOf(a.name);
    const bIndex = SLACK_AUTH_STRATEGY_ORDER.indexOf(b.name);
    const aOrder = aIndex >= 0 ? aIndex : SLACK_AUTH_STRATEGY_ORDER.length;
    const bOrder = bIndex >= 0 ? bIndex : SLACK_AUTH_STRATEGY_ORDER.length;
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    return a.name.localeCompare(b.name);
  });
};

const dedupeSlackCredentialStrategies = <T extends { name: string }>(
  strategies: T[]
): T[] => {
  const hasSlackAuth = strategies.some((s) => s.name === SLACK_AUTH_STRATEGY);
  if (hasSlackAuth) {
    return sortSlackAuthStrategies(
      strategies.filter(
        (s) =>
          s.name !== SLACK_OAUTH_STRATEGY &&
          s.name !== SLACK_SEARCH_COCO_INSTALL_STRATEGY
      )
    );
  }

  const hasSlackOAuth = strategies.some((s) => s.name === SLACK_OAUTH_STRATEGY);
  if (!hasSlackOAuth) {
    return strategies;
  }
  return sortSlackAuthStrategies(
    strategies.filter((s) => s.name !== SLACK_SEARCH_COCO_INSTALL_STRATEGY)
  );
};

/**
 * Converts tool auth strategy names to AuthStrategyInfo objects for ToolInfoCard.
 * Determines which strategies are "active" based on tool execution status.
 */
function buildAuthStrategies(
  strategies: Array<{ name: string; displayLabel?: string }>,
  canExecute: boolean
): AuthStrategyInfo[] {
  const hasBuiltIn = strategies.some((s) => s.name === 'platform_managed');

  return strategies.map((strategy) => {
    const isBuiltIn = strategy.name === 'platform_managed';
    const isActive = canExecute && (isBuiltIn || !hasBuiltIn);

    return {
      name: strategy.name,
      isActive,
      displayLabel: strategy.displayLabel,
    };
  });
}

/**
 * Wrapper component for ToolInfoCard used in the provider detail page accordion.
 * Handles data transformation from AvailableTool to ToolInfoCardProps.
 */
export const ToolAccordionItem: React.FC<{
  tool: AvailableTool;
  authStrategies: Array<{ name: string; displayLabel?: string }>;
  isHighlighted?: boolean;
  onSelect?: () => void;
}> = ({ tool, authStrategies, isHighlighted, onSelect }) => {
  const authStrategyInfos = useMemo(
    () => buildAuthStrategies(authStrategies, tool.canExecute),
    [authStrategies, tool.canExecute]
  );

  return (
    <ToolInfoCard
      name={tool.name}
      description={tool.defaultDescription ?? tool.description}
      toolKey={tool.key}
      inputTy={tool.inputTy}
      outputTy={tool.outputTy}
      inputFieldDescriptions={tool.inputFieldDescriptions}
      authStrategies={authStrategyInfos}
      canExecute={tool.canExecute}
      notExecutableReason={tool.notExecutableReason}
      variant="accordion"
      highlighted={isHighlighted}
      onSelect={onSelect}
    />
  );
};

/**
 * Connection state for the header. A provider is "configured" as soon as one
 * tool runs, so a flat "Connected" overstates a provider whose credentials only
 * cover part of its catalog — or whose saved credentials have stopped working
 * entirely.
 */
const ReadinessBadge: React.FC<{ provider: ProviderGroup }> = ({
  provider,
}) => {
  // Bound to a const so the switch narrows it; re-calling in `default` would
  // hand `Assert.unreachable` the wide type instead of `never`.
  const readiness = providerReadiness(provider);
  switch (readiness) {
    case 'all':
      return <ToolConnectionStatusBadge label="Connected" />;
    case 'partial':
      return (
        <Chip appearance="inline" variant="warning">
          {provider.configuredToolCount} of {provider.totalToolCount} tools
          ready
        </Chip>
      );
    case 'none':
      return (
        <ToolConnectionStatusBadge
          label="Needs attention"
          status="not-connected"
        />
      );
    default:
      return Assert.unreachable(readiness);
  }
};

/**
 * Everything about one provider — connection state, credential forms and its
 * tool list — with no modal chrome of its own, so it can sit in the catalog's
 * right pane or fill a pushed modal page.
 */
export const ProviderDetailPanel: React.FC<{
  provider: ProviderGroup;
  expandedToolKey?: string;
  onToolSelect?: (tool: AvailableTool) => void;
  className?: string;
  /**
   * Trims the hero header for the catalog's right pane, where the modal title
   * already establishes context and vertical space is scarce.
   */
  compact?: boolean;
}> = ({
  provider,
  expandedToolKey,
  onToolSelect,
  className,
  compact = false,
}) => {
  const Icon =
    PROVIDER_ICONS[provider.provider as keyof typeof PROVIDER_ICONS] ??
    (() => null);

  const description = provider.description;
  const [toolFilter, setToolFilter] = useState('');

  // Extract unique credential strategies from all tools
  // Key by name + sorted field names to distinguish strategies with different fields
  const uniqueStrategies = useMemo(() => {
    const strategyMap = new Map<
      string,
      {
        name: string;
        toolCount: number;
        toolKey: string;
        fieldNames: string[];
        displayLabel?: string;
      }
    >();
    for (const tool of provider.tools) {
      for (const strategy of tool.credentialStrategyDescriptors) {
        const fields = [...(strategy.fieldNames ?? [])].sort();
        const dedupKey = `${strategy.name}:${fields.join(',')}`;
        const existing = strategyMap.get(dedupKey);
        if (existing !== undefined) {
          existing.toolCount++;
        } else {
          strategyMap.set(dedupKey, {
            name: strategy.name,
            toolCount: 1,
            toolKey: tool.key,
            fieldNames: fields,
            displayLabel: strategy.displayLabel,
          });
        }
      }
    }
    return dedupeSlackCredentialStrategies(Array.from(strategyMap.values()));
  }, [provider.tools]);

  const hasMultipleStrategies = uniqueStrategies.length > 1;

  // Check if this is a no-auth provider (all strategies are built-in or no auth)
  const filteredStrategies = uniqueStrategies.filter(
    (s) => !isLegacyStrategy(s.name)
  );
  const isNoAuthProvider =
    filteredStrategies.length > 0 &&
    filteredStrategies.every((s) =>
      ['platform_managed', 'none'].includes(s.name)
    );

  // Get docs URL for this provider
  const docsUrl = getProviderDocsUrl(provider.provider);

  const readiness = providerReadiness(provider);

  const visibleTools = useMemo(
    () => provider.tools.filter((tool) => toolMatchesFilter(tool, toolFilter)),
    [provider.tools, toolFilter]
  );

  return (
    <div
      className={cn(
        'flex flex-1 flex-col h-full min-h-0',
        compact ? 'px-6 py-5' : 'px-8 py-8',
        className
      )}
    >
      <div
        className={cn(
          'border-b border-border shrink-0',
          compact ? 'pb-4 mb-4' : 'pb-8 mb-8'
        )}
      >
        <div className={cn('flex items-start', compact ? 'gap-4' : 'gap-5')}>
          <div
            className={cn(
              'shrink-0 flex items-center justify-center rounded-lg bg-muted/50 border border-border',
              compact ? 'w-12 h-12' : 'w-20 h-20'
            )}
          >
            <div className={compact ? 'scale-125' : 'scale-[2]'}>
              <Icon />
            </div>
          </div>
          {/* Title and description */}
          <div className="flex-1 min-w-0 pt-0.5">
            <h2
              className={cn('font-semibold', compact ? 'text-lg' : 'text-2xl')}
            >
              {provider.displayName}
            </h2>
            {description !== undefined && (
              <p
                className={cn(
                  'text-muted-foreground',
                  compact ? 'text-sm mt-1 line-clamp-2' : 'text-base mt-2'
                )}
              >
                {description}
              </p>
            )}
            <div
              className={cn(
                'flex items-center flex-wrap gap-x-3 gap-y-1.5',
                compact ? 'mt-2' : 'mt-3'
              )}
            >
              <span className="text-sm text-muted-foreground">
                {provider.totalToolCount} tools available
              </span>
              {usesCocoSlackInstall(provider.provider) ? (
                <SlackCocoInstalledBadge />
              ) : (
                provider.configStatus === 'configured' && (
                  <ReadinessBadge provider={provider} />
                )
              )}
              {docsUrl !== null && (
                <>
                  <span className="text-muted-foreground/40">•</span>
                  <a
                    href={docsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 transition-colors font-medium"
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                    <span>View Documentation</span>
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* Connection. Open when it wants something from you, folded away when
            it does not: on a working provider this is settled state, and it was
            holding the top of the pane above the tools you came for. */}
        <Collapsible
          defaultOpen={readiness !== 'all'}
          className={compact ? 'mb-7' : 'mb-10'}
        >
          <CollapsibleTrigger
            className={cn(
              'group flex w-full items-center gap-2 font-semibold',
              compact ? 'text-sm mb-3' : 'text-lg mb-5'
            )}
          >
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90" />
            <span>
              {isNoAuthProvider
                ? 'Status'
                : readiness === 'all'
                ? 'Connection'
                : `Connect ${provider.displayName}`}
            </span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-8">
                  <Loading.Spinner />
                </div>
              }
            >
              <ProviderAuthStrategies
                provider={provider}
                uniqueStrategies={uniqueStrategies}
                hasMultipleStrategies={hasMultipleStrategies}
              />
            </Suspense>
          </CollapsibleContent>
        </Collapsible>

        {/* Tools section - accordion for inline schema viewing */}
        <div>
          <div className="flex items-center justify-between gap-4 mb-3">
            <h3
              className={cn('font-semibold', compact ? 'text-sm' : 'text-lg')}
            >
              Tools Included ({provider.totalToolCount})
            </h3>
            {/* Present for every provider, but collapsed to its icon: a
                threshold put near-identical providers on either side of it, and
                an always-open box was chrome on the many providers with only a
                handful of tools. */}
            <CollapsibleSearch
              label="Filter tools"
              placeholder="Filter tools…"
              value={toolFilter}
              onChange={setToolFilter}
            />
          </div>
          {visibleTools.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No tools match &ldquo;{toolFilter}&rdquo;.
            </p>
          ) : (
            <Accordion
              // Re-mount so a newly requested tool opens even when the panel is
              // already showing this provider (the catalog reuses the pane).
              key={expandedToolKey ?? provider.provider}
              type="single"
              collapsible
              defaultValue={expandedToolKey}
              className="flex flex-col gap-2"
            >
              {visibleTools.map((tool) => {
                // Get non-legacy auth strategies for this tool
                const authStrategies = tool.credentialStrategyDescriptors
                  .filter((s) => !isLegacyStrategy(s.name))
                  .map((s) => ({
                    name: s.name,
                    displayLabel: s.displayLabel,
                  }));

                return (
                  <ToolAccordionItem
                    key={tool.key}
                    tool={tool}
                    authStrategies={authStrategies}
                    isHighlighted={tool.key === expandedToolKey}
                    onSelect={
                      onToolSelect !== undefined
                        ? () => onToolSelect(tool)
                        : undefined
                    }
                  />
                );
              })}
            </Accordion>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * The provider panel as a standalone modal page, for the flows that still push
 * a provider straight onto the modal stack (chat tool chips, loaded-tools).
 */
export const ProviderDetailPage: React.FC<{
  provider: ProviderGroup;
  expandedToolKey?: string;
  onToolSelect?: (tool: AvailableTool) => void;
}> = ({ provider, expandedToolKey, onToolSelect }) => (
  <ModalPageView className="flex flex-col h-full max-w-none">
    <ProviderDetailPanel
      provider={provider}
      expandedToolKey={expandedToolKey}
      onToolSelect={onToolSelect}
    />
  </ModalPageView>
);

/**
 * Expandable section for adding credentials with optional strategy toggle.
 */
const AddCredentialsSection: React.FC<{
  expanded: boolean;
  onExpand: () => void;
  buttonLabel: string;
  toolKey: string;
  strategyNames: string[];
  selectedDisplayName: string;
  onStrategyChange: (displayName: string | undefined) => void;
  onSuccess: () => void;
}> = ({
  expanded,
  onExpand,
  buttonLabel,
  toolKey,
  strategyNames,
  selectedDisplayName,
  onStrategyChange,
  onSuccess,
}) => {
  if (!expanded) {
    return (
      <button
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
        onClick={onExpand}
      >
        <Plus className="h-4 w-4" />
        <span>{buttonLabel}</span>
      </button>
    );
  }

  return (
    <CredentialFormPanel
      toolKey={toolKey}
      strategyNames={strategyNames}
      selectedDisplayName={selectedDisplayName}
      onStrategyChange={onStrategyChange}
      onSuccess={onSuccess}
    />
  );
};

/**
 * Bordered panel containing strategy toggle and credential form.
 */
const CredentialFormPanel: React.FC<{
  toolKey: string;
  strategyNames: string[];
  selectedDisplayName: string;
  onStrategyChange: (displayName: string | undefined) => void;
  onSuccess: () => void;
}> = ({
  toolKey,
  strategyNames,
  selectedDisplayName,
  onStrategyChange,
  onSuccess,
}) => (
  <div className="border border-border rounded-xl p-6 bg-muted/10">
    {strategyNames.length > 1 && (
      <div className="mb-6">
        <ToggleSelect
          options={strategyNames}
          value={selectedDisplayName}
          onChange={onStrategyChange}
          nullable={false}
        />
      </div>
    )}
    <Suspense fallback={<Loading.Spinner />}>
      <ProviderCredentialForm
        toolKey={toolKey}
        preferredStrategy={selectedDisplayName}
        onSuccess={onSuccess}
      />
    </Suspense>
  </div>
);

type ProviderAccountSummary = {
  id: string;
  displayName: string;
  status: string;
  createdAt: string;
};

type AuthState =
  | { type: 'no-auth'; hasBuiltIn: boolean }
  | { type: 'built-in-only' }
  | {
      type: 'has-accounts';
      accounts: Array<ProviderAccountSummary>;
    }
  | {
      type: 'needs-attention';
      accounts: Array<ProviderAccountSummary>;
    }
  | { type: 'needs-setup'; toolKey: string }
  | { type: 'no-strategies' };

function getAuthState(
  isNoAuth: boolean,
  hasBuiltInStrategy: boolean,
  configStatus: string,
  configuredToolCount: number,
  accounts: Array<ProviderAccountSummary>,
  firstToolKey: string | undefined
): AuthState {
  if (isNoAuth) {
    return { type: 'no-auth', hasBuiltIn: hasBuiltInStrategy };
  }
  if (
    configStatus === 'configured' &&
    accounts.length === 0 &&
    hasBuiltInStrategy
  ) {
    return { type: 'built-in-only' };
  }
  if (accounts.length > 0) {
    // An account exists but none of the provider's tools are actually
    // executable — the saved credentials are broken (e.g. a missing subdomain).
    // Surface that instead of a flat "Connected", which is what made a broken
    // account read as healthy.
    if (configuredToolCount === 0) {
      return { type: 'needs-attention', accounts };
    }
    return { type: 'has-accounts', accounts };
  }
  if (firstToolKey === undefined) {
    return { type: 'no-strategies' };
  }
  return { type: 'needs-setup', toolKey: firstToolKey };
}

// Component to show and manage auth strategies for a provider
const ProviderAuthStrategies: React.FC<{
  provider: ProviderGroup;
  uniqueStrategies: Array<{
    name: string;
    toolCount: number;
    toolKey: string;
    fieldNames: string[];
    displayLabel?: string;
  }>;
  hasMultipleStrategies: boolean;
}> = ({ provider, uniqueStrategies }) => {
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);

  const { data: accountsData } = useProviderAccounts(provider.provider);
  const accounts = accountsData.accounts;

  const { displayStrategies, isNoAuth } = filterStrategies(uniqueStrategies);

  const strategyDisplayNames = useMemo(() => {
    return displayStrategies.map((s) =>
      s.displayLabel !== undefined
        ? s.displayLabel
        : getAuthStrategyLabel(s.name, 'long')
    );
  }, [displayStrategies]);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showAddAnother, setShowAddAnother] = useState(false);

  const selectedDisplayName = strategyDisplayNames[selectedIndex] ?? '';
  const selectedToolKey = displayStrategies[selectedIndex]?.toolKey;

  const handleToggleChange = (displayName: string | undefined) => {
    if (displayName === undefined) {
      return;
    }
    const idx = strategyDisplayNames.indexOf(displayName);
    if (idx >= 0) {
      setSelectedIndex(idx);
    }
  };

  const handleSuccess = () => {
    // Broadcast the credential change; the tool-credential-updated query-sync
    // watchable refetches the affected tool caches (tools list, provider
    // accounts, credential info, slack install) — no manual invalidation here.
    emitToolCredentialUpdated(orgId, provider.provider);
    // Optimistically update tools cache to mark this provider's tools as executable
    // This makes the warning triangle disappear instantly, ahead of the refetch.
    queryClient.setQueryData(
      queryKeys.toolsV2.all({ orgId }),
      (
        oldData:
          | Array<{ key: string; provider: string; canExecute: boolean }>
          | undefined
      ) => {
        if (oldData === undefined) {
          return oldData;
        }
        return oldData.map((tool) =>
          tool.provider === provider.provider
            ? { ...tool, canExecute: true, notExecutableReason: null }
            : tool
        );
      }
    );

    setShowAddAnother(false);
  };

  const hasBuiltInStrategy = uniqueStrategies.some(
    (s) => s.name === 'platform_managed'
  );
  const canAddCredentials =
    displayStrategies.length > 0 && selectedToolKey !== undefined;

  const authState = getAuthState(
    isNoAuth,
    hasBuiltInStrategy,
    provider.configStatus,
    provider.configuredToolCount,
    accounts,
    selectedToolKey
  );

  const isCocoSlackInstallOnlySetup =
    authState.type === 'needs-setup' &&
    displayStrategies.length === 1 &&
    (displayStrategies[0]?.name === SLACK_OAUTH_STRATEGY ||
      displayStrategies[0]?.name === SLACK_AUTH_STRATEGY);

  const credentialFormProps = canAddCredentials
    ? {
        expanded: showAddAnother,
        onExpand: () => setShowAddAnother(true),
        toolKey: selectedToolKey,
        strategyNames: strategyDisplayNames,
        selectedDisplayName,
        onStrategyChange: handleToggleChange,
        onSuccess: handleSuccess,
      }
    : null;

  switch (authState.type) {
    case 'no-auth':
      return (
        <ConnectedStatusCard
          title={authState.hasBuiltIn ? 'Cotera Built-in' : 'Ready to Use'}
          description={
            authState.hasBuiltIn
              ? 'These tools are included with your Cotera instance'
              : 'No authentication required for these tools'
          }
        />
      );

    case 'built-in-only':
      return (
        <div className="flex flex-col gap-4">
          <ConnectedStatusCard
            title="Cotera Built-in"
            description="These tools work with Cotera's built-in credentials"
          />
          {credentialFormProps !== null && (
            <AddCredentialsSection
              {...credentialFormProps}
              buttonLabel="Use your own credentials instead"
            />
          )}
        </div>
      );

    case 'has-accounts':
      return (
        <div className="flex flex-col gap-4">
          <ProviderAccountCards
            provider={provider.provider}
            accounts={authState.accounts}
          />
          {credentialFormProps !== null && (
            <AddCredentialsSection
              {...credentialFormProps}
              buttonLabel="Add another account"
            />
          )}
        </div>
      );

    case 'needs-attention':
      return (
        <div className="flex flex-col gap-4">
          <ToolConnectionStatusCard
            title="Reconnect required"
            description="This connection is saved but none of its tools are configured — the credentials are likely invalid or missing a required field (e.g. subdomain). Re-enter them to continue."
            status="not-connected"
            statusLabel="Needs attention"
          />
          <ProviderAccountCards
            provider={provider.provider}
            accounts={authState.accounts}
          />
          {credentialFormProps !== null && (
            <AddCredentialsSection
              {...credentialFormProps}
              buttonLabel="Update credentials"
            />
          )}
        </div>
      );

    case 'no-strategies':
      return (
        <div className="text-muted-foreground">
          No authentication methods available.
        </div>
      );

    case 'needs-setup':
      if (isCocoSlackInstallOnlySetup) {
        return (
          <Suspense fallback={<Loading.Spinner />}>
            <ProviderCredentialForm
              toolKey={authState.toolKey}
              preferredStrategy={selectedDisplayName}
              onSuccess={handleSuccess}
            />
          </Suspense>
        );
      }

      return (
        <CredentialFormPanel
          toolKey={authState.toolKey}
          strategyNames={strategyDisplayNames}
          selectedDisplayName={selectedDisplayName}
          onStrategyChange={handleToggleChange}
          onSuccess={handleSuccess}
        />
      );
  }
};

/**
 * Wrapper component for CredentialForm used in the provider detail page.
 * Handles data fetching and API calls while delegating form rendering to the base component.
 */
const ProviderCredentialForm: React.FC<{
  toolKey: string;
  preferredStrategy?: string;
  onSuccess: () => void;
}> = ({ toolKey, preferredStrategy, onSuccess }) => {
  const client = useTenantedClient();
  const { data: toolInfo } = useToolCredentialInfo(toolKey);

  // Find the preferred strategy by displayName (needed when multiple strategies share the same name),
  // falling back to name match, then first strategy
  const strategy =
    toolInfo.strategies.find((s) => s.displayName === preferredStrategy) ??
    toolInfo.strategies.find((s) => s.strategy.name === preferredStrategy) ??
    toolInfo.strategies[0];

  if (strategy === undefined) {
    return (
      <div className="text-muted-foreground">
        No authentication methods available.
      </div>
    );
  }

  const docsUrl = getProviderDocsUrl(toolInfo.provider);

  // Route to the custom strategy section for OAuth connects (WorkOS/Slack) and
  // for any custom strategy that registers its own install view (e.g. Linear's
  // `linear_oauth`). Otherwise these render an empty credential form because
  // their `credentialFields` are `[]`, making the toggle appear to do nothing.
  const hasCustomStrategyView =
    strategy.strategy.t === 'custom' &&
    strategy.strategy.name in CUSTOM_CREDENTIAL_STRATEGY_VIEWS;
  const isOAuthStrategy = isCredentialStrategyOauthConnect(strategy.strategy);

  if (isOAuthStrategy || hasCustomStrategyView) {
    return (
      <CredentialStrategySection
        toolKey={toolKey}
        provider={toolInfo.provider}
        toolDisplayName={toolInfo.displayName}
        strategy={strategy}
        docsUrl={docsUrl}
        onSuccess={() => {
          toast.success('Connection configured successfully');
          onSuccess();
        }}
      />
    );
  }

  const handleSubmit = async (credentials: Record<string, string>) => {
    const result = await client.toolsV2.createProviderAccount({
      toolKey,
      strategyName: strategy.strategy.name,
      credentials,
    });

    if (result.isErr()) {
      const errorObj = result.error.err;
      throw new Error(
        'message' in errorObj && errorObj.message
          ? errorObj.message
          : 'Failed to save credentials'
      );
    }

    toast.success('Connection configured successfully');
    onSuccess();
  };

  return (
    <CredentialForm
      provider={toolInfo.provider}
      strategyName={strategy.strategy.name}
      fields={strategy.credentialFields}
      docsUrl={docsUrl}
      onSubmit={handleSubmit}
    />
  );
};
